
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";
import {
    Box,
    Card,
    CardContent,
    TextField,
    Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { DataGrid } from "@mui/x-data-grid";

import { getAuthToken } from "../../utils/token";
import config from "../../config/config";
import ProductionPlanDetailsDialog from "../seniors/ProductionPlanDetailsDialog";

/**
 * =========================================================
 * FORMAT DATE AS DD/MM/YYYY
 * =========================================================
 */
const formatDate = (date) => {
    if (!date) return "";

    const dateString = String(date).substring(0, 10);
    const parts = dateString.split("-");

    if (parts.length !== 3) {
        return String(date);
    }

    const [year, month, day] = parts;

    return `${day}/${month}/${year}`;
};

/**
 * =========================================================
 * PRODUCTION DISPATCH PLAN
 * =========================================================
 */
const ProductionDispatchPlan = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    /**
     * =====================================================
     * ACTUAL DP DATE FILTERS
     * =====================================================
     */
    const [actualDpFrom, setActualDpFrom] = useState("");
    const [actualDpTo, setActualDpTo] = useState("");

    /**
     * =====================================================
     * SEARCH
     * =====================================================
     */
    const [searchText, setSearchText] = useState("");

    /**
     * =====================================================
     * PAGINATION
     * =====================================================
     */
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    /**
     * =====================================================
     * DETAILS DIALOG
     * =====================================================
     */
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedPOrderId, setSelectedPOrderId] =
        useState(null);

    /**
     * =====================================================
     * HANDLE ROW CLICK
     * =====================================================
     */
    const handleRowClick = (params) => {
        // Do not open dialog for total row
        if (params.row.isTotalRow) {
            return;
        }

        const pOrderId = params.row.erp_po_id;

        console.log("Clicked Row:", params.row);
        console.log("POrderId:", pOrderId);

        if (pOrderId == null) {
            console.error(
                "erp_po_id not found:",
                params.row
            );
            return;
        }

        setSelectedPOrderId(pOrderId);
        setDetailsOpen(true);
    };

    /**
     * =====================================================
     * FETCH PRODUCTION DISPATCH PLANS
     * =====================================================
     */
    const fetchDispatchPlans = useCallback(async () => {
        try {
            setLoading(true);

            const token = getAuthToken();

            if (!token) {
                setRows([]);
                return;
            }

            const response = await axios.get(
                `${config.API}/productiondispatchplan/index`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },

                    /**
                     * Stored procedure parameters:
                     *
                     * p_comp_code
                     * p_actual_dp_from
                     * p_actual_dp_to
                     *
                     * comp_code comes from logged-in user
                     * in Laravel.
                     */
                    params: {
                        actual_dp_from:
                            actualDpFrom || null,

                        actual_dp_to:
                            actualDpTo || null,
                    },
                }
            );

            const apiRows = response.data?.data;

            setRows(
                Array.isArray(apiRows)
                    ? apiRows
                    : []
            );
        } catch (error) {
            console.error(
                "Error fetching production dispatch plans:",
                error.response?.data || error
            );

            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [actualDpFrom, actualDpTo]);

    /**
     * =====================================================
     * FETCH DATA WHEN DATE FILTER CHANGES
     * =====================================================
     */
    useEffect(() => {
        fetchDispatchPlans();
    }, [fetchDispatchPlans]);

    /**
     * =====================================================
     * SEARCH
     * =====================================================
     */
    const searchedRows = useMemo(() => {
        const search = searchText
            .trim()
            .toLowerCase();

        if (!search) {
            return rows;
        }

        return rows.filter((row) => {
            const item = String(
                row.item || ""
            ).toLowerCase();

            const poNo = String(
                row.PO_No || ""
            ).toLowerCase();

            /**
             * Search Actual DP
             */
            const actualDp = formatDate(
                row.actual_dp
            ).toLowerCase();

            /**
             * Search Actual Delivery Date
             */
            const actualDeliveryDate = formatDate(
                row.actual_delivery_date
            ).toLowerCase();

            /**
             * Search Commense Date
             */
            const commenseDate = formatDate(
                row.commense_date
            ).toLowerCase();

            return (
                item.includes(search) ||
                poNo.includes(search) ||
                actualDp.includes(search) ||
                actualDeliveryDate.includes(search) ||
                commenseDate.includes(search)
            );
        });
    }, [rows, searchText]);

    /**
     * =====================================================
     * GROUP BY PO NUMBER
     *
     * Add total row after each PO
     * =====================================================
     */
    const groupedRows = useMemo(() => {
        const result = [];
        const poGroups = {};

        searchedRows.forEach((row) => {
            const poNo = row.PO_No || "N/A";

            if (!poGroups[poNo]) {
                poGroups[poNo] = [];
            }

            poGroups[poNo].push(row);
        });

        Object.entries(poGroups).forEach(
            ([poNo, poRows]) => {
                let totalOrderQty = 0;
                let totalDispatchQty = 0;
                let totalPendingQty = 0;

                poRows.forEach((row, index) => {
                    totalOrderQty += Number(
                        row.total_order_qty || 0
                    );

                    totalDispatchQty += Number(
                        row.total_dispatch_qty || 0
                    );

                    totalPendingQty += Number(
                        row.pending_qty || 0
                    );

                    result.push({
                        ...row,

                        /**
                         * Ensure unique DataGrid ID
                         */
                        id:
                            row.id ??
                            `${row.erp_po_id || poNo}-${index}`,

                        isTotalRow: false,
                    });
                });

                /**
                 * =================================================
                 * PO TOTAL ROW
                 * =================================================
                 */
                result.push({
                    id: `TOTAL-${poNo}`,

                    item: "",
                    product_code: "",

                    PO_No: "TOTAL",

                    total_order_qty: totalOrderQty,
                    total_dispatch_qty: totalDispatchQty,
                    pending_qty: totalPendingQty,

                    consignee: "",

                    isTotalRow: true,
                });
            }
        );

        return result;
    }, [searchedRows]);

    /**
     * =====================================================
     * DATAGRID COLUMNS
     * =====================================================
     */
    const columns = useMemo(
        () => [
             {
                field: "product_code",
                headerName: "ItemCode",
                flex: 2,
                minWidth: 100,
            },
            
            {
                field: "item",
                headerName: "Item",
                flex: 2,
                minWidth: 120,
            },

            {
                field: "PO_No",
                headerName: "PO No",
                flex: 1.2,
                minWidth: 110,
            },

            {
                field: "total_order_qty",
                headerName: "Order",
                flex: 0.7,
                minWidth: 75,
            },

            {
                field: "pending_qty",
                headerName: "Pending",
                flex: 0.7,
                minWidth: 75,
            },

             {
                field: "production_start_date",
                headerName: "Prd Start",
                flex: 1,
                minWidth: 110,

                renderCell: (params) =>
                    formatDate(params.value),
            },
              {
                field: "inspection_date",
                headerName: "Inspection",
                flex: 1,
                minWidth: 110,

                renderCell: (params) =>
                    formatDate(params.value),
            },
              {
                field: "commense_date",
                headerName: "Commense",
                flex: 1,
                minWidth: 110,

                renderCell: (params) =>
                    formatDate(params.value),
            },

          {
                field: "actual_delivery_date",
                headerName: "ActualDP",
                flex: 1,
                minWidth: 110,

                renderCell: (params) =>
                    formatDate(params.value),
            },

            {
                field: "actual_dp",
                headerName: "PlanDP",
                flex: 1,
                minWidth: 110,

                renderCell: (params) =>
                    formatDate(params.value),
            },
    {
    field: "planstatus",
    headerName: "Status",
    flex: 0.7,
    minWidth: 100,
    
    renderCell: (params) => (
        <Chip
            size="small"
            label={params.value?.replace("_", " ") || "-"}
            color={
                params.value === "pending"
                    ? "success"
                    : params.value === "not_started"
                    ? "error"
                    : "default"
            }
        />
    ),
}
        ],
        []
    );

    /**
     * =====================================================
     * RENDER
     * =====================================================
     */
    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
            }}
        >
            <Card
                elevation={2}
                sx={{
                    width: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                }}
            >
                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <CardContent
                    sx={{
                        p: 2,

                        "&:last-child": {
                            pb: 2,
                        },

                        borderBottom:
                            "1px solid #e5e7eb",

                        backgroundColor: "#fff",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%",
                            flexWrap: "wrap",
                        }}
                    >
                        {/* ================================================= */}
                        {/* TITLE */}
                        {/* ================================================= */}

                        <Box
                            sx={{
                                flexShrink: 0,

                                minWidth: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                            }}
                        >
                          
                        </Box>

                        {/* ================================================= */}
                        {/* DATE FILTERS */}
                        {/* ================================================= */}

                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                                <TextField
            type="date"
            size="small"
            value={actualDpFrom}
            onChange={(event) => {
                setActualDpFrom(event.target.value);

                setPaginationModel((prev) => ({
                    ...prev,
                    page: 0,
                }));
            }}
            placeholder="Actual DP From"
            sx={{
                width: 150,
            }}
        />

                        <TextField
                            type="date"
                            size="small"
                            value={actualDpTo}
                            onChange={(event) => {
                                setActualDpTo(event.target.value);

                                setPaginationModel((prev) => ({
                                    ...prev,
                                    page: 0,
                                }));
                            }}
                            placeholder="Actual DP To"
                            sx={{
                                width: 150,
                            }}
                        />
                        </Box>

                        {/* ================================================= */}
                        {/* SEARCH */}
                        {/* ================================================= */}

                        <Box
                            sx={{
                                flex: 1,

                                minWidth: {
                                    xs: "100%",
                                    sm: 300,
                                    md: 350,
                                },
                            }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                label="Search by Item, PO No, Actual DP, Commense Date"
                                value={searchText}
                                onChange={(event) => {
                                    setSearchText(
                                        event.target.value
                                    );

                                    /**
                                     * Return to first page
                                     * when search changes.
                                     */
                                    setPaginationModel(
                                        (prev) => ({
                                            ...prev,
                                            page: 0,
                                        })
                                    );
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>

                {/* ================================================= */}
                {/* DATAGRID */}
                {/* ================================================= */}

                <CardContent
                    sx={{
                        p: 1,

                        "&:last-child": {
                            pb: 1,
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            overflowX: "auto",
                        }}
                    >
                        <DataGrid
                            rows={groupedRows}
                            columns={columns}
                            loading={loading}
                            getRowId={(row) => row.id}
                            autoHeight
                            density="compact"
                            rowHeight={32}
                            columnHeaderHeight={38}

                            paginationModel={
                                paginationModel
                            }

                            onPaginationModelChange={
                                setPaginationModel
                            }

                            pageSizeOptions={[
                                10,
                                25,
                                50,
                                100,
                            ]}

                            disableRowSelectionOnClick

                            onRowClick={
                                handleRowClick
                            }

                            getRowClassName={(params) =>
                                params.row.isTotalRow
                                    ? "total-row"
                                    : "clickable-row"
                            }

                            sx={{
                                border: 0,

                                "& .MuiDataGrid-columnHeaders":
                                    {
                                        backgroundColor:
                                            "#f1f5f9",

                                        borderBottom:
                                            "2px solid #1976d2",
                                    },

                                "& .MuiDataGrid-columnHeaderTitle":
                                    {
                                        fontWeight: 700,
                                        fontSize: "12px",
                                    },

                                "& .MuiDataGrid-cell":
                                    {
                                        fontSize: "12px",
                                    },

                                "& .MuiDataGrid-row:hover":
                                    {
                                        backgroundColor:
                                            "#f8fbff",
                                    },

                                "& .clickable-row":
                                    {
                                        cursor: "pointer",
                                    },

                                "& .total-row":
                                    {
                                        backgroundColor:
                                            "#fff8e1",

                                        fontWeight: 700,

                                        cursor: "default",
                                    },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* ================================================= */}
            {/* DETAILS DIALOG */}
            {/* ================================================= */}

            <ProductionPlanDetailsDialog
                open={detailsOpen}
                poId={selectedPOrderId}
                onClose={() => {
                    setDetailsOpen(false);
                    setSelectedPOrderId(null);
                }}
            />
        </Box>
    );
};

export default ProductionDispatchPlan;
